import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Button from 'components/field/Button';
import ProgressWizard from 'components/field/ProgressWizard';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalTitle } from 'components/global/styled/Modal.styled';

import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import AccountBillingDetails from 'pages/Settings/General/AccountBillingDetails';
import useBillingDetailsValidation from 'pages/Settings/Payment/hooks/useBillingValidation';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';

import AppLogger from 'utils/AppLogger';
import { Breakpoints } from 'utils/const.styles';
import { PAYMENT_GATEWAY_TYPE, PAYMENT_METHODS } from 'utils/constants';
import PATHS from 'utils/paths';
import { formatPrice } from 'utils/utils';

import { faCheck, faChevronLeft, faFileInvoice, faShoppingBasket } from '@fortawesome/pro-solid-svg-icons';

import * as S from './ChargeAccountModal.styled';
import PaymentFormButton from './PaymentFormButton';
import OrderSummary from './Tabs/OrderSummary';
import Result from './Tabs/Result';
import SelectAmount from './Tabs/SelectAmount';
import SelectPaymentMethod from './Tabs/SelectPaymentMethod';

/**
 * @author @muhammedburakak
 */

const TABS = {
	selectAmount: 'chargeAccount.selectAmount',
	billingInfo: 'chargeAccount.billingInfo',
	selectPaymentMethod: 'chargeAccount.selectPaymentMethod',
	completed: 'chargeAccount.completed'
};

const ChargeAccountModal = NiceModal.create(({ modalName, mode, defaultSelectedAmount }) => {
	const { closeModal } = useModalRouter();
	const { t } = useTranslation();
	const isTablet = useMediaQuery(Breakpoints.tablet);
	const modal = useModal();
	const { payment, paymentResult } = useSelector((state) => state?.chargeAccount);
	const { bankTransferReceiverAccount, bankTransferAvailable, paymentGatewayType, minimumChargeAmount } = useSelector(
		(state) => state?.user.localization
	);
	const {
		localization: { vatPercentage }
	} = useSelector((state) => state.user);
	const { pathname } = useLocation();
	const { Currency } = useLocale();

	const [selectedIndex, setSelectedIndex] = useState(defaultSelectedAmount ? 1 : 0);
	const [isSectionCompleted, setIsSectionCompleted] = useState(!!defaultSelectedAmount);
	const { isBillingValid, billingDetailsSchema, billingDefaults } = useBillingDetailsValidation();
	const schema = useMemo(
		() =>
			yup.object().shape({
				selectedPaymentMethod: yup.string().nullable(),
				promoCode: yup.string().nullable(),
				subTotal: yup.number().nullable(),
				vatAmount: yup.number().nullable(),
				total: yup.number().nullable(),
				freeCredit: yup.number().nullable(),
				codWallet: yup.number().nullable(),
				isPromoCodeApplied: yup.boolean().nullable(),
				selectedSavedCard: yup.object().shape({
					card: yup.string().nullable(),
					cvvRequired: yup.boolean()
				}),
				selectedOption: yup.string(),
				isCodPaymentSuccess: yup.boolean().default(false),
				cardHolderName: yup
					.string()
					.nullable()
					.when('selectedPaymentMethod', ([selectedPaymentMethod], schema) =>
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR &&
						selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD
							? schema.required()
							: schema
					),
				cardNumber: yup
					.string()
					.nullable()
					.min(16)
					.max(20)
					.transform((value) => value?.replace(/\s/g, ''))
					.when('selectedPaymentMethod', ([selectedPaymentMethod], schema) =>
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR &&
						selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD
							? schema.required()
							: schema
					),
				cvv: yup
					.string()
					.nullable()
					.min(3)
					.max(3)
					.when('selectedPaymentMethod', ([selectedPaymentMethod], schema) =>
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR &&
						selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD
							? schema.required()
							: schema
					),
				cardExpiry: yup
					.string()
					.nullable()
					.matches(/^(0[1-9]|1[0-2])\/[2-9]\d$/g, t('validation.invalidExpiryDate'))
					.when('selectedPaymentMethod', ([selectedPaymentMethod], schema) =>
						paymentGatewayType === PAYMENT_GATEWAY_TYPE.PAYTR &&
						selectedPaymentMethod === PAYMENT_METHODS.CREDIT_CARD
							? schema.required()
							: schema
					),
				saveCard: yup.boolean().nullable(),
				cvvForSavedCard: yup
					.string()
					.nullable()
					.min(3)
					.max(3)
					.when('selectedSavedCard.cvvRequired', {
						is: true,
						then: (schema) => schema.required()
					}),
				receipt: yup.object().nullable()
			}),
		[paymentGatewayType]
	);

	const bankTransferReceiverAccountData = JSON.parse(
		bankTransferAvailable && bankTransferReceiverAccount ? bankTransferReceiverAccount : '{}'
	);

	const formSchema = useMemo(() => {
		if (isBillingValid) return schema;
		if (selectedIndex === 1) return billingDetailsSchema;
		return schema;
	}, [isBillingValid, selectedIndex]);

	const formMethods = useForm({
		resolver: yupResolver(formSchema),
		defaultValues: {
			...bankTransferReceiverAccountData,
			...payment,
			...(isBillingValid ? {} : billingDefaults),
			isCodPaymentSuccess: false,
			cardHolderName: null,
			cardNumber: null,
			cvv: null,
			cvvForSavedCard: null,
			cardExpiry: null,
			saveCard: false,
			selectedPaymentMethod: null,
			promoCode: null,
			subTotal: 0,
			vatAmount: 0,
			total: 0,
			freeCredit: null,
			codWallet: null,
			isPromoCodeApplied: false,
			isTapInputsValid: false,
			selectedSavedCard: {
				card: null,
				cvvRequired: false
			},
			selectedOption: 'custom',
			receipt: null
		}
	});

	const {
		watch,
		setValue,
		formState: { errors, isDirty, isSubmitSuccessful, isSubmitting },
		handleSubmit
	} = formMethods;

	usePrompt(isDirty && !isSubmitSuccessful && !isSubmitting, {
		title: 'youHaveUnsavedChangesCOD',
		subTitle: 'areYouSureYouWantToLeaveCOD'
	});

	const handleCloseModal = () => {
		if (modalName) {
			closeModal();
		} else {
			modal.resolve();
			modal.remove();
		}
	};

	useEffect(() => {
		if (import.meta.env.VITE_PLATFORM === 'desktop' && modal.visible) {
			if (modal.visible) {
				handleCloseModal();
			}
			window?.electronAPI?.openExternalBrowser?.(
				`https://${import.meta.env.VITE_ENVIRONMENT === 'production' ? 'app' : 'staging-api'}.tryoto.com/?modal=charge-account-form&oto-token=${localStorage.getItem('ACCESS_TOKEN')}`
			);
		}
	}, [modal.visible]);

	useEffect(() => {
		if (defaultSelectedAmount) {
			let value = Number(defaultSelectedAmount);
			if (value < minimumChargeAmount || 0) {
				value = minimumChargeAmount;
			}
			const vat = +((value * vatPercentage) / 100).toFixed(2);
			setValue('subTotal', value, { shouldValidate: true, shouldDirty: true });
			setValue('vatAmount', vat, { shouldValidate: true, shouldDirty: true });
			setValue('total', +(value + vat).toFixed(2), { shouldValidate: true, shouldDirty: true });
		}
	}, [defaultSelectedAmount]);

	const chargeAccountTabs = useMemo(
		() => [
			{ name: TABS.selectAmount, icon: faShoppingBasket, description: 'chargeAccount.selectAmountDescription' },
			...(!isBillingValid
				? [
						{
							fields: [
								'address',
								'VATNumber',
								'CRNumber',
								'CRDocumentURL',
								'companyName',
								'taxOffice',
								'notVATRegistered',
								'invoiceType',
								'civilID',
								'fullName',
								'idNumber'
							],
							name: TABS.billingInfo,
							icon: faFileInvoice,
							description: 'chargeAccount.billingInfoDescription'
						}
					]
				: []),
			{
				name: TABS.selectPaymentMethod,
				icon: faFileInvoice,
				description: 'chargeAccount.selectPaymentDescription',
				fields: [
					'selectedSavedCard',
					'selectedPaymentMethod',
					'cardHolderName',
					'cardNumber',
					'cvv',
					'cardExpiry',
					'saveCard',
					'cvvForSavedCard'
				]
			},
			{ name: TABS.completed, icon: faCheck, success: true }
		],
		[isBillingValid]
	);

	const nextStep = () => {
		if (selectedIndex + 1 < chargeAccountTabs.length) {
			if (activeTab === TABS.selectAmount) setSelectedIndex(selectedIndex + 1);
			else handleSubmit(() => setSelectedIndex(selectedIndex + 1), AppLogger.log)();
		}
	};

	const prevStep = () => {
		if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
	};

	const goToStep = (tabName) => {
		const index = chargeAccountTabs.findIndex((tab) => tab.name === tabName);
		if (index !== -1) setSelectedIndex(index);
	};

	const activeTab = useMemo(() => chargeAccountTabs[selectedIndex]?.name, [chargeAccountTabs, selectedIndex]);

	const isSuccessPayment = useMemo(() => {
		if (TABS.completed === activeTab) {
			if (
				paymentResult?.status === 'paid' ||
				mode === 'paytrsuccess' ||
				(watch('selectedPaymentMethod') === PAYMENT_METHODS.BANK_CARD && paymentResult?.status === 'pending') ||
				watch('codWallet') === watch('total')
			) {
				if (pathname === PATHS.CREATE_SHIPMENT) {
					handleCloseModal();
				}

				return true;
			}
		}
		return false;
	}, [activeTab, paymentResult?.status, mode, watch]);

	const isFailedPayment = useMemo(
		() => TABS.completed === activeTab && (paymentResult?.status === 'failed' || mode === 'paytrfail'),
		[activeTab, paymentResult?.status, mode]
	);

	const isFreeCreditPromotionApplied = useMemo(
		() => TABS.completed === activeTab && watch('isPromoCodeApplied'),
		[watch, activeTab]
	);

	const renderTab = useCallback(
		(index) => {
			const tab = chargeAccountTabs[index];
			switch (tab.name) {
				case TABS.selectAmount:
					return (
						<SelectAmount
							goToSuccessTab={() => goToStep(TABS.completed)}
							setIsSectionCompleted={setIsSectionCompleted}
						/>
					);
				case TABS.billingInfo:
					return <AccountBillingDetails chargeAccount />;
				case TABS.selectPaymentMethod:
					return <SelectPaymentMethod nextStep={() => setSelectedIndex(selectedIndex + 1)} />;
				case TABS.completed:
					return <Result />;
				default:
					return null;
			}
		},
		[chargeAccountTabs, errors, selectedIndex]
	);

	useEffect(() => {
		fetchRemainingAmount();
	}, []);

	return (
		<Modal
			name={modalName}
			maxWidth="x-large"
			fullwidth
			className="modal-chargeAccount"
			minimizeButton={(show, close) => <MinimizeButton onShow={show} onClose={close} label="chargeAccount" />}>
			<ModalTitle>
				<Typography variant="p2" fontWeight="500" className="modal-title">
					chargeAccount
				</Typography>
				{!isTablet && (
					<ProgressWizard
						stages={chargeAccountTabs}
						index={selectedIndex}
						setIndex={setSelectedIndex}
						clickable={false}
						gutterBottom
					/>
				)}
			</ModalTitle>
			{isTablet && (
				<ProgressWizard
					className={'mt-4'}
					stages={chargeAccountTabs}
					index={selectedIndex}
					setIndex={setSelectedIndex}
					clickable={false}
					gutterBottom
					showDescription
				/>
			)}

			<S.FormContainer>
				<FormProvider {...formMethods}>
					<S.TabContainer>{renderTab(selectedIndex)}</S.TabContainer>
					{TABS.completed !== activeTab && (
						<S.SummaryContainer>
							<OrderSummary goToSuccessTab={() => goToStep(TABS.completed)} />
						</S.SummaryContainer>
					)}
				</FormProvider>
			</S.FormContainer>
			<FormProvider {...formMethods}>
				<ModalActions>
					{activeTab !== TABS.completed && (
						<Button
							variant="blackOutline"
							label={isSuccessPayment ? 'done' : 'cancel'}
							onClick={handleCloseModal}
							css={{
								textDecoration: 'underline',
								border: 'none'
							}}
						/>
					)}

					{TABS.completed !== activeTab && selectedIndex > 0 && (
						<Button
							variant="outline"
							label="previous"
							icon={faChevronLeft}
							onClick={prevStep}
							className="icon-prev"
						/>
					)}

					{TABS.completed !== activeTab && (
						<PaymentFormButton
							nextStep={nextStep}
							label={
								watch('codWallet') ? (
									activeTab === TABS.selectPaymentMethod ? (
										'confirm'
									) : (
										'continue'
									)
								) : watch('total') > 0 ? (
									<Trans
										i18nKey="chargeAccount.payAmount"
										values={{
											value: formatPrice(watch('total'), Currency),
											currency: t(Currency)
										}}
									/>
								) : (
									'continue'
								)
							}
							disabled={!isSectionCompleted || activeTab === TABS.completed}
							isPaymentStep={activeTab === TABS.selectPaymentMethod}
							disabledTooltip={
								minimumChargeAmount &&
								minimumChargeAmount > watch('subTotal') && (
									<Trans
										i18nKey="minimumChargeAmountTooltip"
										values={{
											minAmount: formatPrice(minimumChargeAmount, Currency),
											currency: t(Currency)
										}}
									/>
								)
							}
						/>
					)}

					<ResultButton
						prevStep={prevStep}
						isPaymentError={isFailedPayment}
						isSuccess={isSuccessPayment || isFreeCreditPromotionApplied}
						closeModal={handleCloseModal}
						selectedPaymentMethod={watch('selectedPaymentMethod')}
					/>
				</ModalActions>
			</FormProvider>
		</Modal>
	);
});

const ResultButton = ({ prevStep, isPaymentError, closeModal, isSuccess, selectedPaymentMethod }) => {
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const goCreateShipment = () => {
		closeModal();

		const isShipmentPage = [
			PATHS.PRICE_CALCULATOR,
			PATHS.SHIPMENTS_PRICE_CALCULATOR,
			PATHS.IMPORT_CALCULATOR,
			PATHS.IMPORT_ORDERS_GET_ESTIMATE,
			PATHS.CREATE_SHIPMENT
		].includes(pathname);

		if (!isShipmentPage) {
			navigate(PATHS.PRICE_CALCULATOR);
		}
	};

	return (
		<>
			{isPaymentError && <Button variant="blue" label="paymentErrorButton" onClick={prevStep} />}

			{isSuccess && (
				<>
					<Button
						variant="blackOutline"
						label="done"
						onClick={() => closeModal()}
						css={{
							textDecoration: 'underline',
							border: 'none'
						}}
					/>
					{selectedPaymentMethod !== PAYMENT_METHODS.BANK_CARD && (
						<Button variant="blue" label="createShipment" onClick={goCreateShipment} />
					)}
				</>
			)}
		</>
	);
};

export default ChargeAccountModal;
